import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

// ─────────────────────────────────────────────────────────────
// Seuil d'alerte de stock faible, réglable PAR MARCHAND.
// Stocké dans users.stock_alert_threshold (défaut 5). Utilisé par la
// page de gestion de stock, la page mouvements et les notifications.
// ─────────────────────────────────────────────────────────────

export const DEFAULT_STOCK_THRESHOLD = 5;

export interface StockThresholdApi {
  threshold: number;
  loading: boolean;
  saving: boolean;
  save: (value: number) => Promise<void>;
  userId: number | null;
}

export function useStockThreshold(): StockThresholdApi {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [threshold, setThreshold] = useState<number>(DEFAULT_STOCK_THRESHOLD);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    if (!userId) {
      setThreshold(DEFAULT_STOCK_THRESHOLD);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    supabase
      .from('users')
      .select('stock_alert_threshold')
      .eq('id', userId)
      .maybeSingle()
      .then(({ data }) => {
        if (!active) return;
        const raw = Number((data as { stock_alert_threshold?: number } | null)?.stock_alert_threshold);
        setThreshold(Number.isFinite(raw) && raw >= 0 ? raw : DEFAULT_STOCK_THRESHOLD);
      })
      .catch(() => { /* garde la valeur par défaut */ })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userId]);

  const save = useCallback(async (value: number) => {
    if (!userId) return;
    const clean = Math.max(0, Math.round(Number.isFinite(value) ? value : DEFAULT_STOCK_THRESHOLD));
    setSaving(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({ stock_alert_threshold: clean })
        .eq('id', userId);
      if (error) throw error;
      setThreshold(clean);
    } finally {
      setSaving(false);
    }
  }, [userId]);

  return { threshold, loading, saving, save, userId };
}

export default useStockThreshold;